// Monta uma sala a partir da planta (RoomMap): fundo assado (piso + paredes + itens de
// parede), Drawables dos móveis, pontos de uso e grade de caminhada.

import { stableHash } from '../../core/hash.js';
import { TILE, WALL_H, mapSize, type RoomMap, type Spot } from '../../core/world.js';
import { STATION_SCREENS } from '../art/furniture/office.js';
import { FURNITURE_BY_ID } from '../art/furniture/index.js';
import { resolveToken } from '../art/palette.js';
import { TILE_BY_ID } from '../art/tiles.js';
import type { FurnitureDef } from '../art/types.js';
import { bake, ctx2d, makeCanvas } from './bake.js';
import { roomData, spotTile } from './room-data.js';
import type { Drawable } from './scene.js';

/** Espessura do topo de parede visto de cima (a borda escura das salas). */
export const WALL_CAP = 3;

/** Tela de um monitor de estação: quem senta no `seat` (tile da sala) vê a animação dela. */
export interface BuiltScreen {
  seat: { x: number; y: number };
  /** Retângulo da tela no mundo (px). */
  box: { x: number; y: number; w: number; h: number };
  /** Logo depois da mesa, para ser pintada por cima dela. */
  sortY: number;
}

export interface BuiltRoom {
  map: RoomMap;
  /** Origem do piso (canto superior esquerdo do tile 0,0) no mundo. */
  ox: number;
  oy: number;
  background: { canvas: HTMLCanvasElement; x: number; y: number };
  drawables: Drawable[];
  /** Telas dos monitores de estação desta sala. */
  screens: BuiltScreen[];
  /** Pontos de uso, em tiles da sala. */
  spots: Spot[];
  /** walkable[y][x]: dá para pisar no tile? */
  walkable: boolean[][];
  /** Brilhos noturnos (centro em mundo). */
  glows: Array<{ x: number; y: number; color: string; radius: number }>;
}

const color = (token: string) => resolveToken(token) ?? '#ff00ff';

export function buildRoom(map: RoomMap, ox: number, oy: number): BuiltRoom {
  const { w, h } = mapSize(map);
  const canvas = makeCanvas(w * TILE + WALL_CAP * 2, h * TILE + WALL_H + WALL_CAP * 2);
  const ctx = ctx2d(canvas);
  // origem do piso dentro do canvas
  const fx = WALL_CAP;
  const fy = WALL_H + WALL_CAP;
  const inside = (x: number, y: number) => (map.floor[y]?.[x] ?? ' ') !== ' ';

  // piso
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!inside(x, y)) continue;
      const tile = TILE_BY_ID.get(map.legend[map.floor[y]?.[x] ?? ''] ?? '');
      if (!tile) continue;
      const options = [tile.sprite, ...(tile.variants ?? [])];
      const pick = options[stableHash(`${map.id}:${x}:${y}`) % options.length] ?? tile.sprite;
      ctx.drawImage(bake(pick), fx + x * TILE, fy + y * TILE);
    }
  }
  // paredes: toda borda do piso vira parede. A de cima (norte) tem face com altura
  // WALL_H; as outras aparecem só como o topo escuro, visto de cima (corte da maquete).
  const isDoor = (x: number, y: number) => map.doors.some((d) => d.x === x && d.y === y);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (map.openTop || !inside(x, y) || inside(x, y - 1)) continue;
      const px = fx + x * TILE;
      const py = fy + y * TILE;
      ctx.fillStyle = color('wall2');
      ctx.fillRect(px, py - WALL_H, TILE, WALL_H);
      ctx.fillStyle = color('wall3');
      ctx.fillRect(px, py - WALL_H, TILE, 1);
      // lambri: metade de baixo mais escura, com friso
      ctx.fillStyle = color('wall1');
      ctx.fillRect(px, py - 9, TILE, 6);
      ctx.fillStyle = color('wall3');
      ctx.fillRect(px, py - 10, TILE, 1);
      ctx.fillStyle = 'rgba(0,0,0,0.06)';
      ctx.fillRect(px + 15, py - 9, 1, 6);
      ctx.fillStyle = color('wood1');
      ctx.fillRect(px, py - 3, TILE, 3);
      ctx.fillStyle = color('wood0');
      ctx.fillRect(px, py - 1, TILE, 1);
      ctx.fillStyle = 'rgba(0,0,0,0.16)';
      ctx.fillRect(px, py, TILE, 4); // sombra da parede no piso
    }
  }
  ctx.fillStyle = color('wall0');
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!inside(x, y)) continue;
      const px = fx + x * TILE;
      const py = fy + y * TILE;
      const top = !map.openTop && !inside(x, y - 1);
      const rise = top ? WALL_H + WALL_CAP : 0; // as laterais sobem junto com a face norte
      if (!inside(x - 1, y)) ctx.fillRect(px - WALL_CAP, py - rise, WALL_CAP, TILE + rise);
      if (!inside(x + 1, y)) ctx.fillRect(px + TILE, py - rise, WALL_CAP, TILE + rise);
      if (top) ctx.fillRect(px, py - WALL_H - WALL_CAP, TILE, WALL_CAP);
      if (!inside(x, y + 1) && !isDoor(x, y)) {
        const l = inside(x - 1, y) ? 0 : WALL_CAP;
        const r = inside(x + 1, y) ? 0 : WALL_CAP;
        ctx.fillRect(px - l, py + TILE, TILE + l + r, WALL_CAP);
      }
    }
  }

  const data = roomData(map);
  const walkable = data.walkable;
  const spots: Spot[] = data.spots;
  const drawables: Drawable[] = [];
  const screens: BuiltScreen[] = [];
  const glows: BuiltRoom['glows'] = [];

  map.items.forEach((placement) => {
    const def = FURNITURE_BY_ID.get(placement.item);
    if (!def) return;
    const sw = def.sprite.w;
    const sh = def.sprite.h;
    const left = ox + placement.x * TILE + Math.floor((def.footprint.w * TILE - sw) / 2);
    if (def.wall) {
      // centralizado na face da parede, acima do rodapé
      const top = oy - WALL_H + Math.max(1, Math.floor((WALL_H - 4 - sh) / 2));
      const art = bake(def.sprite, { flip: placement.flip });
      ctx.drawImage(art, left - ox + fx, top - oy + fy);
      if (def.glow) glows.push({ x: left + sw / 2 + (def.glow.dx ?? 0), y: top + sh / 2 + (def.glow.dy ?? 0), color: def.glow.color, radius: def.glow.radius });
      return;
    }
    const bottom = oy + (placement.y + def.footprint.h) * TILE;
    drawables.push(furnitureDrawable(def, left, bottom - sh, bottom + (def.sortBias ?? 0), placement.flip ?? false));
    const boxes = STATION_SCREENS[placement.item];
    const first = def.spots?.[0];
    if (boxes && first) {
      const seat = spotTile(def, placement, first);
      for (const b of boxes) {
        const sx = placement.flip ? sw - b.x - b.w : b.x;
        screens.push({ seat, box: { x: left + sx, y: bottom - sh + b.y, w: b.w, h: b.h }, sortY: bottom + (def.sortBias ?? 0) + 0.5 });
      }
    }
    if (def.glow) glows.push({ x: left + sw / 2 + (def.glow.dx ?? 0), y: bottom - sh / 2 + (def.glow.dy ?? 0), color: def.glow.color, radius: def.glow.radius });
  });

  return { map, ox, oy, background: { canvas, x: ox - fx, y: oy - fy }, drawables, screens, spots, walkable, glows };
}

function furnitureDrawable(def: FurnitureDef, x: number, y: number, sortY: number, flip: boolean): Drawable {
  const frames = [def.sprite, ...(def.frames ?? [])];
  const ms = def.frameMs ?? 800;
  return {
    sortY,
    draw(ctx, now) {
      const sprite = frames.length > 1 ? frames[Math.floor(now / ms) % frames.length] : def.sprite;
      if (sprite) ctx.drawImage(bake(sprite, { flip }), x, y);
    },
  };
}

/** Posição do pé de quem está num ponto (centro do tile, perto da base). Mundo. */
export function spotFeet(room: BuiltRoom, spot: Spot): { x: number; y: number } {
  const seated = spot.kind !== 'standing';
  return { x: room.ox + spot.x * TILE + TILE / 2, y: room.oy + spot.y * TILE + (seated ? 12 : 14) };
}
